/**
 * Overview tab's "Absence Rate by Tenure" chart — bars per tenure band vs.
 * a dashed company-average reference line, plus an optional "Compare last
 * year" bar per band.
 *
 * Split into its own file (out of AbsenceInsightsCharts.tsx) to keep that
 * file under the repo's max-lines lint limit, same reasoning as
 * DayOfWeekChart.tsx and AbsenceOrgTrendChart.tsx.
 */
import { useState } from 'react';
import { Chart } from 'react-chartjs-2';
import {
  BarController,
  BarElement,
  CategoryScale,
  Chart as ChartJS,
  Legend,
  LinearScale,
  LineController,
  LineElement,
  PointElement,
  Tooltip,
} from 'chart.js';
import type { AbsenceType, AbsenceTenureBreakdown } from '../services/absenceApi';
import {
  SICK_LEAVE_COLOR,
  CHILD_CARE_COLOR,
  PRIOR_YEAR_COLOR,
  pickRatePct,
  TogglePill,
  EmptyChartCard,
} from './AbsenceInsightsCharts';
import { InfoTooltip } from './InfoTooltip';

// Idempotent — safe even though AbsenceInsightsCharts.tsx also registers
// these; avoids relying on import order between the two files. The generic
// `Chart` component (needed here for a mixed bar+line dataset) does NOT
// auto-register controllers the way the typed `Bar`/`Line` components do.
ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, BarElement, BarController, LineController, Tooltip, Legend);

// Same muted grey used for "not the highlighted bar" elsewhere — duplicated
// rather than exported from AbsenceInsightsCharts.tsx, matching the existing
// precedent in DayOfWeekChart.tsx.
const MUTED_DARK = '#D7D7D7'; // pewter

export function TenureChart({
  current,
  prior,
  companyAverageRatePct,
  absenceType,
}: {
  current: AbsenceTenureBreakdown[];
  prior: AbsenceTenureBreakdown[];
  companyAverageRatePct: number | null;
  absenceType: AbsenceType;
}) {
  const [showPrior, setShowPrior] = useState(false);

  if (current.length === 0) {
    return <EmptyChartCard title="Absence Rate by Tenure" message="No data in the selected window." />;
  }

  // Same peak-highlight treatment as the volume chart: the highest-rate
  // band(s) get trustedBlue, everything else is muted grey. A suppressed
  // band's real rate is unknown, so it's treated as 0 here — it can never be
  // highlighted as the peak.
  const rates = current.map((r) => pickRatePct(r, absenceType) ?? 0);
  const maxRate = Math.max(...rates, 0);
  const isPeak = rates.map((r) => r >= maxRate - 0.01 && maxRate > 0);

  // Matched by tenure band name, not array position — the set of bands
  // present can in principle differ between the current and prior windows.
  const priorByBand = new Map(prior.map((r) => [r.tenureBand, r]));

  // Prior-year peak — computed off last year's own rates (independent of the
  // current year's peak band, which can differ), so toggling "Compare last
  // year" shows which band was highest that year too, in lightBlue.
  const priorRate = (band: string) => {
    const p = priorByBand.get(band);
    return p ? pickRatePct(p, absenceType) ?? 0 : 0;
  };
  const maxPriorRate = Math.max(...prior.map((r) => pickRatePct(r, absenceType) ?? 0), 0);
  const isPriorPeak = current.map(
    (r) => priorByBand.has(r.tenureBand) && priorRate(r.tenureBand) >= maxPriorRate - 0.01 && maxPriorRate > 0
  );

  // Parallel to `datasets` below (same push order) — lets the tooltip
  // callback tell "GDPR-suppressed" apart from "genuinely no data".
  const suppressedByDataset: boolean[][] = [current.map((r) => r.suppressed)];
  const datasets: import('chart.js').ChartDataset<'bar' | 'line'>[] = [
    {
      type: 'bar' as const,
      label: 'Absence Rate',
      data: current.map((r) => pickRatePct(r, absenceType)),
      backgroundColor: isPeak.map((peak) => (peak ? SICK_LEAVE_COLOR : MUTED_DARK)),
      borderRadius: 4,
      order: 2,
    },
  ];
  if (showPrior) {
    suppressedByDataset.push(current.map((r) => priorByBand.get(r.tenureBand)?.suppressed ?? false));
    datasets.push({
      type: 'bar' as const,
      label: 'Absence Rate (last year)',
      data: current.map((r) => {
        const p = priorByBand.get(r.tenureBand);
        return p ? pickRatePct(p, absenceType) : null;
      }),
      backgroundColor: isPriorPeak.map((peak) => (peak ? CHILD_CARE_COLOR : PRIOR_YEAR_COLOR)),
      borderRadius: 4,
      order: 2,
    });
  }
  if (companyAverageRatePct !== null) {
    suppressedByDataset.push(current.map(() => false));
    datasets.push({
      type: 'line' as const,
      label: 'Company Average',
      data: current.map(() => companyAverageRatePct),
      borderColor: PRIOR_YEAR_COLOR,
      borderDash: [6, 4],
      borderWidth: 2,
      pointRadius: 0,
      pointHoverRadius: 0,
      fill: false,
      order: 1,
    });
  }

  return (
    <section className="rounded-xl border border-zinc-200 bg-white p-6 shadow-sm">
      <div className="flex items-baseline justify-between">
        <div>
          <h2 className="text-sm font-semibold uppercase tracking-wide text-zinc-500">
            Absence Rate by Tenure
            <InfoTooltip text="Absence rate (%) grouped by employee tenure band, compared against the dashed company-wide average. Tenure is evaluated per employee per reporting month, so someone's band updates automatically as they cross a tenure milestone." />
          </h2>
          <p className="mt-1 text-xs text-zinc-400">
            Absence rate (%) by employee tenure band, vs. the company-wide average
          </p>
        </div>
        <TogglePill active={showPrior} onClick={() => setShowPrior((v) => !v)}>
          Compare previous year
        </TogglePill>
      </div>
      <div className="mt-4" style={{ height: 220 }}>
        <Chart
          type="bar"
          data={{
            // Two short lines instead of one long string — Chart.js auto-rotates
            // a single-line label that doesn't fit, which read as diagonal,
            // hard-to-read text under each bar. An array label renders as
            // separate horizontal lines instead.
            labels: current.map((r) => [r.tenureBand, `Avg. monthly headcount = ${r.employeeCount}`]),
            datasets,
          }}
          options={{
            responsive: true,
            maintainAspectRatio: false,
            plugins: {
              legend: { position: 'bottom', labels: { boxWidth: 10 } },
              tooltip: {
                callbacks: {
                  label: (context) => {
                    const suppressed = suppressedByDataset[context.datasetIndex]?.[context.dataIndex];
                    if (suppressed) return `${context.dataset.label}: Hidden for privacy reasons`;
                    return `${context.dataset.label}: ${context.formattedValue}%`;
                  },
                },
              },
            },
            scales: {
              x: { ticks: { maxRotation: 0, minRotation: 0 } },
              y: { beginAtZero: true, ticks: { callback: (v) => `${v}%` } },
            },
          }}
        />
      </div>
    </section>
  );
}
